import React, { useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRightIcon, CheckIcon, UserGroupIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { usePlan } from '../../hooks/usePlan'
import { useAuth } from '../../context/AuthContext'
import {
  CENNIK, DARMOWY_DOSTEP, DNI_PROBY, FUNKCJE_PREMIUM, KOLEJNOSC_FUNKCJI, KONTAKT_PREMIUM, LIMIT_KONT, PROBA_BEZ_FIRMY,
  nazwaFunkcji,
} from '../../config/plany'
import { formatKwota } from '../../utils'
import { CenaPremium, IKONY_FUNKCJI, PrzelacznikOkresu } from './ElementyPlanow'
import OdbierzPremium, { dataPremium } from './OdbierzPremium'

/**
 * Okno „Przejdź na Premium” (FE-02). Jedno dla całego serwisu: otwiera je kłódka przy funkcji, karta Premium
 * w miejscu funkcji i odpowiedź 402 z backendu (`useOknoPremium().otworz(funkcja)`, OknoPremiumContext).
 *
 * Nowy wygląd 2026-10-19 (prośba właściciela): funkcja, która otworzyła okno, stoi pierwsza na liście z wyróżnioną
 * ikoną i opisem; cena z przełącznikiem miesięcznie / rocznie; jeden główny przycisk.
 *
 * Płatności jeszcze nie ma (FP-12). Od 2026-10-19 (decyzja właściciela, `DARMOWY_DOSTEP`) główny przycisk to
 * „Odbierz darmowy dostęp”: miesiąc Premium za darmo, od razu, bez wiadomości do nas. Po wyłączeniu flagi wraca
 * wiadomość „Napisz do nas o Premium” (Premium włączamy wtedy ręcznie, zasada „uczciwe wkrótce”).
 */

const tematMaila = (firma) => encodeURIComponent(`Premium dla firmy ${firma || ''}`.trim())
const trescMaila = (firma, nip, okres) => encodeURIComponent(
  `Dzień dobry,\n\nchcemy przejść na Premium.\nFirma: ${firma || ''}\nNIP: ${nip || ''}\nPlan: ${okres === 'rok' ? 'roczny' : 'miesięczny'}\n`,
)

const Funkcja = ({ klucz, wybrana }) => {
  const Ikona = IKONY_FUNKCJI[klucz]
  return (
    <li className={`flex items-start gap-2.5 sm:gap-3 ${wybrana ? 'col-span-2' : ''}`}>
      <span aria-hidden="true"
            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] sm:h-9 sm:w-9
                        ${wybrana ? 'bg-blue-600 text-white shadow-[0_4px_12px_rgba(37,99,235,0.30)]' : 'bg-gray-100 text-gray-700'}`}>
        {Ikona && <Ikona className="h-[18px] w-[18px]" />}
      </span>
      <span className="flex min-w-0 flex-col pt-1 sm:pt-1.5">
        <span className={`text-[13px] leading-5 sm:text-sm ${wybrana ? 'font-semibold text-gray-900' : 'text-gray-800'}`}>{FUNKCJE_PREMIUM[klucz].nazwa}</span>
        {wybrana && <span className="mt-0.5 text-[13px] leading-5 text-gray-600">{FUNKCJE_PREMIUM[klucz].opis}</span>}
      </span>
    </li>
  )
}

const OknoPremium = ({ funkcja = null, onZamknij }) => {
  const tytulId = useId()
  const zamknijRef = useRef(null)
  const plan = usePlan()
  const { profil } = useAuth()
  const firma = profil?.company || null
  const [okres, setOkres] = useState('miesiac')
  const [odebrano, setOdebrano] = useState(null)

  useEffect(() => {
    zamknijRef.current?.focus()
    const esc = (e) => { if (e.key === 'Escape') onZamknij() }
    document.addEventListener('keydown', esc)
    return () => document.removeEventListener('keydown', esc)
  }, [onZamknij])

  // Konto bez firmy odbiera Premium samo, gdy działa darmowy dostęp i próba bez firmy; inaczej Premium jest dla firm.
  const prywatne = plan.stan === 'plan' && !plan.firmowe && !(DARMOWY_DOSTEP && PROBA_BEZ_FIRMY)
  const wybrana = funkcja && FUNKCJE_PREMIUM[funkcja] ? funkcja : null
  const lista = wybrana ? [wybrana, ...KOLEJNOSC_FUNKCJI.filter((k) => k !== wybrana)] : KOLEJNOSC_FUNKCJI

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-gray-900/50 backdrop-blur-[2px] sm:items-center sm:p-4"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onZamknij() }}>
      <div role="dialog" aria-modal="true" aria-labelledby={tytulId}
           className="relative max-h-[92vh] w-full max-w-[580px] overflow-y-auto rounded-t-[24px] bg-white
                      shadow-[0_24px_64px_rgba(15,23,42,0.28)] sm:rounded-[24px]">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-blue-600 via-blue-500 to-amber-400" />
        <button ref={zamknijRef} type="button" onClick={onZamknij} aria-label="Zamknij"
                className="absolute right-3 top-4 flex h-10 w-10 items-center justify-center rounded-full text-gray-500
                           hover:bg-gray-100 hover:text-gray-800">
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>

        <div className="px-5 pb-5 pt-5 sm:px-8 sm:pb-8 sm:pt-7">
          <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-amber-700">
            <span className="h-2 w-2 rounded-full bg-amber-400" aria-hidden="true" />Premium
          </span>
          <h2 id={tytulId} className="m-0 mt-2 pr-10 text-[21px] font-bold leading-tight tracking-[-0.01em] text-gray-900 sm:text-[26px]">
            {wybrana ? `${nazwaFunkcji(wybrana)} jest w planie Premium` : 'Przejdź na Premium'}
          </h2>
          <p className="m-0 mt-2 text-sm leading-6 text-gray-600 sm:text-[15px]">
            Dokumenty dla klientów i praca całego zespołu. Jeden plan dla całej firmy.
          </p>

          <ul className="m-0 mt-5 grid list-none grid-cols-2 gap-x-3 gap-y-2.5 p-0 sm:gap-x-5">
            {lista.map((klucz) => <Funkcja key={klucz} klucz={klucz} wybrana={klucz === wybrana} />)}
            <li className="flex items-start gap-2.5 sm:gap-3">
              <span aria-hidden="true" className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-gray-100 text-gray-700 sm:h-9 sm:w-9">
                <UserGroupIcon className="h-[18px] w-[18px]" />
              </span>
              <span className="pt-1 text-[13px] leading-5 text-gray-800 sm:pt-1.5 sm:text-sm"
                    title={`Kolejne ${CENNIK.pakietKont.kont} kont za ${formatKwota(CENNIK.pakietKont.miesiecznie)} netto miesięcznie`}>
                {LIMIT_KONT.premium} kont w firmie zamiast {LIMIT_KONT.free}
              </span>
            </li>
          </ul>

          {odebrano ? (
            <div className="mt-6 flex flex-col items-center border-t border-gray-100 pt-6 text-center">
              <span aria-hidden="true" className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <CheckIcon className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <p role="status" className="m-0 mt-3 text-lg font-bold text-gray-900">Gotowe, masz Premium do {dataPremium(odebrano)}</p>
              <p className="m-0 mt-1 text-sm text-gray-600">Wszystkie funkcje z listy działają już na Twoim koncie.</p>
              <button type="button" onClick={onZamknij}
                      className="mt-5 flex h-[52px] w-full items-center justify-center rounded-xl bg-blue-600 text-base font-semibold
                                 text-white shadow-[0_6px_16px_rgba(37,99,235,0.28)] hover:bg-blue-700">
                Wracam do pracy
              </button>
            </div>
          ) : prywatne ? (
            <div className="mt-7 border-t border-gray-100 pt-6">
              <p className="m-0 text-[15px] leading-6 text-gray-700">
                Premium jest dla firm. Dodaj firmę w ustawieniach konta, a dostaniesz {DNI_PROBY} dni Premium na próbę.
              </p>
              <Link to="/konto?sekcja=firma" onClick={onZamknij}
                    className="mt-5 flex h-[52px] items-center justify-center gap-2 rounded-xl bg-blue-600 text-base font-semibold
                               text-white no-underline shadow-[0_6px_16px_rgba(37,99,235,0.28)] hover:bg-blue-700">
                Dodaj firmę<ArrowRightIcon className="h-[18px] w-[18px]" strokeWidth={2.2} aria-hidden="true" />
              </Link>
            </div>
          ) : DARMOWY_DOSTEP ? (
            <div className="mt-6 border-t border-gray-100 pt-5">
              <p className="m-0 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-lg font-semibold text-gray-400 line-through decoration-2">
                  <span className="sr-only">zamiast </span>{formatKwota(CENNIK.premiumMiesiecznie)}
                </span>
                <span className="text-[34px] font-bold leading-none tracking-[-0.02em] text-gray-900">{formatKwota(0)}</span>
                <span className="text-sm text-gray-500">za miesiąc Premium, bez karty płatniczej</span>
              </p>
              {/* Na telefonie przycisk przykleja się do dołu arkusza, żeby był widoczny bez przewijania listy. */}
              <div className="sticky bottom-0 -mx-5 mt-4 bg-white px-5 pb-1 pt-1 sm:static sm:mx-0 sm:mt-5 sm:p-0">
                <OdbierzPremium rozmiar="duzy" onGotowe={setOdebrano} />
              </div>
              <p className="m-0 mt-3 text-center text-xs leading-5 text-gray-500">
                Dane na koncie zostają, także w planie Free.{' '}
                <Link to="/cennik" onClick={onZamknij} className="font-semibold text-blue-700 no-underline hover:underline">Zobacz cennik</Link>
              </p>
            </div>
          ) : (
            <div className="mt-6 border-t border-gray-100 pt-5">
              <PrzelacznikOkresu okres={okres} onZmiana={setOkres} pelny />
              <div className="mt-4">
                <CenaPremium okres={okres} />
              </div>
              {/* Na telefonie przycisk przykleja się do dołu arkusza, żeby był widoczny bez przewijania listy. */}
              <div className="sticky bottom-0 -mx-5 mt-4 bg-white px-5 pb-1 pt-1 sm:static sm:mx-0 sm:mt-5 sm:p-0">
                <a href={`mailto:${KONTAKT_PREMIUM}?subject=${tematMaila(firma?.name)}&body=${trescMaila(firma?.name, firma?.nip, okres)}`}
                   className="flex h-[52px] items-center justify-center gap-2 rounded-xl bg-blue-600 text-base font-semibold
                              text-white no-underline shadow-[0_6px_16px_rgba(37,99,235,0.28)] hover:bg-blue-700">
                  Napisz do nas o Premium<ArrowRightIcon className="h-[18px] w-[18px]" strokeWidth={2.2} aria-hidden="true" />
                </a>
              </div>
              <p className="m-0 mt-3 text-center text-xs leading-5 text-gray-500">
                Płatności online uruchomimy wkrótce. Do tego czasu włączamy Premium ręcznie po wiadomości.
                Dane na koncie zostają, także w planie Free.{' '}
                <Link to="/cennik" onClick={onZamknij} className="font-semibold text-blue-700 no-underline hover:underline">Zobacz cennik</Link>
              </p>
            </div>
          )}
          {prywatne && !odebrano && (
            <p className="m-0 mt-3 text-center text-xs">
              <Link to="/cennik" onClick={onZamknij} className="font-semibold text-blue-700 no-underline hover:underline">Zobacz cennik</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default OknoPremium
